import {
  type ColumnType,
  type Selectable,
  type Insertable,
  type Updateable,
} from 'kysely'
import { z } from 'zod'

import { serviceItemId, type ServiceItemId } from './ServiceItem.js'
import { userId, type UserId } from './User.js'

/** Identifier type for public.revenue_recognition_ */
export type RevenueRecognitionId = string & { __brand: 'RevenueRecognitionId' }

/** Represents the table public.revenue_recognition_ */
export default interface RevenueRecognitionTable {
  /** Database type: pg_catalog.uuid */
  id: ColumnType<
    RevenueRecognitionId,
    RevenueRecognitionId | undefined,
    RevenueRecognitionId
  >

  /** Database type: pg_catalog.timestamptz */
  createdAt: ColumnType<Date | null, Date | string | null, Date | string | null>

  /** Database type: pg_catalog.uuid */
  createdBy: ColumnType<UserId | null, UserId | null, UserId | null>

  /** Database type: pg_catalog.timestamptz */
  updatedAt: ColumnType<Date | null, Date | string | null, Date | string | null>

  /** Database type: pg_catalog.uuid */
  updatedBy: ColumnType<UserId | null, UserId | null, UserId | null>

  /** Database type: pg_catalog.timestamptz */
  deletedAt: ColumnType<Date | null, Date | string | null, Date | string | null>

  /** Database type: pg_catalog.uuid */
  deletedBy: ColumnType<UserId | null, UserId | null, UserId | null>

  /** Database type: pg_catalog.uuid */
  serviceItemId: ColumnType<ServiceItemId, ServiceItemId, ServiceItemId>

  /** Database type: pg_catalog.timestamptz */
  recognizedAt: ColumnType<Date, Date | string, Date | string>

  /** Database type: pg_catalog.int4 */
  daysRecognized: ColumnType<number, number, number>

  /** Database type: pg_catalog.numeric */
  amountRecognized: ColumnType<number, number, number>

  /** Database type: pg_catalog.text */
  erpnextJournalEntryId: ColumnType<string, string, string>

  /** Database type: pg_catalog.text */
  erpnextSalesOrderId: ColumnType<string | null, string | null, string | null>

  /** Database type: pg_catalog.text */
  erpnextSalesInvoiceId: ColumnType<string | null, string | null, string | null>
}

export type RevenueRecognition = Selectable<RevenueRecognitionTable>

export type RevenueRecognitionInitializer = Insertable<RevenueRecognitionTable>

export type RevenueRecognitionMutator = Updateable<RevenueRecognitionTable>

export const revenueRecognitionId =
  z.string() as unknown as z.Schema<RevenueRecognitionId>

export const revenueRecognition = z.object({
  id: revenueRecognitionId,
  createdAt: z.coerce.date().nullable().optional(),
  createdBy: userId.nullable().optional(),
  updatedAt: z.coerce.date().nullable().optional(),
  updatedBy: userId.nullable().optional(),
  deletedAt: z.coerce.date().nullable().optional(),
  deletedBy: userId.nullable().optional(),
  serviceItemId: serviceItemId,
  recognizedAt: z.coerce.date(),
  daysRecognized: z.number(),
  amountRecognized: z.number(),
  erpnextJournalEntryId: z.string(),
  erpnextSalesOrderId: z.string().nullable().optional(),
  erpnextSalesInvoiceId: z.string().nullable().optional(),
}) as unknown as z.Schema<RevenueRecognition>

export const revenueRecognitionInitializer = z.object({
  id: revenueRecognitionId.optional(),
  createdAt: z.coerce.date().optional().nullable(),
  createdBy: userId.optional().nullable(),
  updatedAt: z.coerce.date().optional().nullable(),
  updatedBy: userId.optional().nullable(),
  deletedAt: z.coerce.date().optional().nullable(),
  deletedBy: userId.optional().nullable(),
  serviceItemId: serviceItemId,
  recognizedAt: z.coerce.date(),
  daysRecognized: z.number(),
  amountRecognized: z.number(),
  erpnextJournalEntryId: z.string(),
  erpnextSalesOrderId: z.string().optional().nullable(),
  erpnextSalesInvoiceId: z.string().optional().nullable(),
}) as unknown as z.Schema<RevenueRecognitionInitializer>

export const revenueRecognitionMutator = z.object({
  id: revenueRecognitionId.optional(),
  createdAt: z.coerce.date().optional().nullable(),
  createdBy: userId.optional().nullable(),
  updatedAt: z.coerce.date().optional().nullable(),
  updatedBy: userId.optional().nullable(),
  deletedAt: z.coerce.date().optional().nullable(),
  deletedBy: userId.optional().nullable(),
  serviceItemId: serviceItemId.optional(),
  recognizedAt: z.coerce.date().optional(),
  daysRecognized: z.number().optional(),
  amountRecognized: z.number().optional(),
  erpnextJournalEntryId: z.string().optional(),
  erpnextSalesOrderId: z.string().optional().nullable(),
  erpnextSalesInvoiceId: z.string().optional().nullable(),
}) as unknown as z.Schema<RevenueRecognitionMutator>
